import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { usePregnancyOrNursing } from '@hooks/register/screening/use-pregnancy-or-nursing';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { pregnancyOrNursingSchema } from '@views/register/screening/pregnancy-or-nursing.schema';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import { asString, asStringBool } from '@/utils/parse';
import type { IPregnancyOrNursingFormValues, IPregnancyOrNursingModel, IPregnancyOrNursingSubmitResult } from '@/types/screening/pregnancy-or-nursing';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

const PregnancyOrNursing = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = usePregnancyOrNursing(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <PregnancyOrNursingContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IPregnancyOrNursingContentProps {
    model: IPregnancyOrNursingModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IPregnancyOrNursingFormValues) => Promise<IPregnancyOrNursingSubmitResult>;
}

const PregnancyOrNursingContent = ({ model, isSubmitting, submitError, onSubmit }: IPregnancyOrNursingContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IPregnancyOrNursingFormValues, IPregnancyOrNursingSubmitResult>({
        mode: 'onChange',
        resolver: zodResolver(pregnancyOrNursingSchema),
        defaultValues: {
            hasBeenPregnant: asStringBool(model.hasBeenPregnant),
            isPregnant: asStringBool(model.isPregnant),
            monthsSincePregnant: asString(model.monthsSincePregnant),
            hadGestationalDiabetes: asStringBool(model.hadGestationalDiabetes),
            isNursing: asStringBool(model.isNursing),
            isPlanningOnPregnancy: asStringBool(model.isPlanningOnPregnancy),
            isTakingBirthControl: asStringBool(model.isTakingBirthControl),
            birthControlForm: model.birthControlForm,
            birthControlMonths: asString(model.birthControlMonths),
            birthControlComplications: model.birthControlComplications,
            isAbleToStopBirthControl: asStringBool(model.isAbleToStopBirthControl),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        watch,
        formState: { errors },
    } = form;

    const hasBeenPregnant = watch('hasBeenPregnant') === '1';
    const showMonthsSincePregnant = hasBeenPregnant && watch('isPregnant') === '0';
    const isTakingBirthControl = watch('isTakingBirthControl') === '1';

    return (
        <StepFormLayout
            kind="screening"
            model={model}
            step={DashRegistrationStep.PregnantOrNursing}
            title={t('DashLabel_ProgressPregnantorNursing', 'Pregnant or Nursing')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t('DashLabel_HaveYouEverBeenPregnant', 'Have you ever been pregnant')}
                    ?<FieldError message={errors.hasBeenPregnant?.message} />
                </label>
                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                    <label className="radio-inline">
                        <input type="radio" value="1" {...register('hasBeenPregnant')} />{' '}
                        {t('Word_Yes', 'Yes')}
                    </label>
                    <label className="radio-inline">
                        <input type="radio" value="0" {...register('hasBeenPregnant')} />{' '}
                        {t('Word_No', 'No')}
                    </label>
                </div>
            </div>

            {hasBeenPregnant && (
                <div id="status" className="sub-question">
                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t(
                                'DashLabel_DuringPregnancyWereYouTreatedForGestationalDiabetes',
                                'During pregnancy were you treated for gestational diabetes',
                            )}
                            ?<FieldError message={errors.hadGestationalDiabetes?.message} />
                        </label>
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            <label className="radio-inline">
                                <input
                                    type="radio"
                                    value="1"
                                    {...register('hadGestationalDiabetes')}
                                />{' '}
                                {t('Word_Yes', 'Yes')}
                            </label>
                            <label className="radio-inline">
                                <input
                                    type="radio"
                                    value="0"
                                    {...register('hadGestationalDiabetes')}
                                />{' '}
                                {t('Word_No', 'No')}
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t('DashLabel_AreYouCurrentlyPregnant', 'Are you currently pregnant')}
                            ?<FieldError message={errors.isPregnant?.message} />
                        </label>
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            <label className="radio-inline">
                                <input type="radio" value="1" {...register('isPregnant')} />{' '}
                                {t('Word_Yes', 'Yes')}
                            </label>
                            <label className="radio-inline">
                                <input type="radio" value="0" {...register('isPregnant')} />{' '}
                                {t('Word_No', 'No')}
                            </label>
                        </div>
                    </div>

                    {showMonthsSincePregnant && (
                        <div id="monthsSincePregnant" className="form-group">
                            <label className="control-label col-xs-12">
                                {t(
                                    'DashLabel_HowManyMonthsSinceYouWerePregnant',
                                    'How many months since you were pregnant',
                                )}
                                ?<FieldError message={errors.monthsSincePregnant?.message} />
                            </label>
                            <div className="col-xs-12" style={{ marginLeft: 10 }}>
                                <select
                                    className="form-control"
                                    style={{ width: 'initial' }}
                                    {...register('monthsSincePregnant')}
                                >
                                    <option value="">
                                        {t('DashLabel_SelectMonths', 'Select Months')}
                                    </option>
                                    {Array.from({ length: 24 }, (_, i) => i + 1).map((month) => (
                                        <option key={month} value={month}>
                                            {month === 24 ? '24+' : month}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t('DashLabel_AreYouCurrentlyNursing', 'Are you currently nursing')}
                            ?<FieldError message={errors.isNursing?.message} />
                        </label>
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            <label className="radio-inline">
                                <input type="radio" value="1" {...register('isNursing')} />{' '}
                                {t('Word_Yes', 'Yes')}
                            </label>
                            <label className="radio-inline">
                                <input type="radio" value="0" {...register('isNursing')} />{' '}
                                {t('Word_No', 'No')}
                            </label>
                        </div>
                    </div>
                </div>
            )}

            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t(
                        'DashLabel_DoYouPlanOnBecomingPregnantInTheNextYear',
                        'Do you plan on becoming pregnant in the next year',
                    )}
                    ?<FieldError message={errors.isPlanningOnPregnancy?.message} />
                </label>
                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                    <label className="radio-inline">
                        <input type="radio" value="1" {...register('isPlanningOnPregnancy')} />{' '}
                        {t('Word_Yes', 'Yes')}
                    </label>
                    <label className="radio-inline">
                        <input type="radio" value="0" {...register('isPlanningOnPregnancy')} />{' '}
                        {t('Word_No', 'No')}
                    </label>
                </div>
            </div>

            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t(
                        'DashLabel_AreYouCurrentlyTakingBirthControl',
                        'Are you currently taking birth control',
                    )}
                    ?<FieldError message={errors.isTakingBirthControl?.message} />
                </label>
                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                    <label className="radio-inline">
                        <input type="radio" value="1" {...register('isTakingBirthControl')} />{' '}
                        {t('Word_Yes', 'Yes')}
                    </label>
                    <label className="radio-inline">
                        <input type="radio" value="0" {...register('isTakingBirthControl')} />{' '}
                        {t('Word_No', 'No')}
                    </label>
                </div>
            </div>

            {isTakingBirthControl && (
                <div id="birthControl" className="sub-question">
                    <div className="form-group">
                        <label className="control-label col-xs-12" htmlFor="BirthControlForm">
                            {t('DashLabel_WhatFormOfBirthControl', 'What form of birth control')}
                            ?<FieldError message={errors.birthControlForm?.message} />
                        </label>
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            <input
                                id="BirthControlForm"
                                className="form-control"
                                {...register('birthControlForm')}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12" htmlFor="BirthControlMonths">
                            {t(
                                'DashLabel_HowManyMonthsHaveYouBeenTakingIt',
                                'How many months have you been taking it',
                            )}
                            ?<FieldError message={errors.birthControlMonths?.message} />
                        </label>
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            <input
                                id="BirthControlMonths"
                                className="form-control"
                                {...register('birthControlMonths')}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label
                            className="control-label col-xs-12"
                            htmlFor="BirthControlComplications"
                        >
                            {t('DashLabel_AnyComplications', 'Any complications')}
                            ?<FieldError message={errors.birthControlComplications?.message} />
                        </label>
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            <input
                                id="BirthControlComplications"
                                className="form-control"
                                {...register('birthControlComplications')}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t('DashLabel_AreYouWillingToStopTakingIt', 'Are you willing to stop taking it')}
                            ?<FieldError message={errors.isAbleToStopBirthControl?.message} />
                        </label>
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            <label className="radio-inline">
                                <input
                                    type="radio"
                                    value="1"
                                    {...register('isAbleToStopBirthControl')}
                                />{' '}
                                {t('Word_Yes', 'Yes')}
                            </label>
                            <label className="radio-inline">
                                <input
                                    type="radio"
                                    value="0"
                                    {...register('isAbleToStopBirthControl')}
                                />{' '}
                                {t('Word_No', 'No')}
                            </label>
                        </div>
                    </div>
                </div>
            )}
        </StepFormLayout>
    );
};

export default PregnancyOrNursing;
